import { ReactNode, useEffect } from 'react';
import { LuX } from 'react-icons/lu';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { useTr } from '../../context/langContext';
import { webStrings } from '../../i18n/web-strings';

/**
 * `showOwnerSheet` on the web: a bottom sheet on mobile, a centred dialog on desktop.
 * Used for every owner form (item editor, slot editor, add manager, new reservation…).
 */
export default function Sheet({
    open,
    onClose,
    title,
    children,
    footer,
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
}) {
    const isDesktop = useIsDesktop();
    const tr = useTr();

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex bg-black/40 backdrop-blur-[1px] ${
                isDesktop ? 'items-center justify-center p-5' : 'items-end'
            }`}
            onClick={onClose}
        >
            <div
                className={`flex max-h-[88vh] w-full flex-col bg-bg ${
                    isDesktop ? 'max-w-lg animate-fade-in rounded-2xl border border-line' : 'animate-slide-up rounded-t-3xl'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start gap-3 px-4 pb-2 pt-4">
                    <p className="flex-1 text-base font-bold text-ink">{title}</p>
                    <button onClick={onClose} className="rounded-lg p-1 text-ink3 hover:bg-surf" aria-label={tr(webStrings, 'close')}>
                        <LuX size={18} />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
                {footer && <div className="border-t border-line p-4">{footer}</div>}
            </div>
        </div>
    );
}
